/* eslint-disable react-hooks/preserve-manual-memoization */
/* eslint-disable max-lines */
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
    ADD, CHANGE, KEY_PRICE, REMOVE
} from 'Component/CategoryFilterOverlay/CategoryFilterOverlay.config';
import history from 'Util/History';
import { getQueryParam, setQueryParams } from 'Util/Url';
/** @namespace Bodypwa/Hooks/UseSeoUrlHelpers/getFilterKey */
export const getFilterKey = (key) => {
    if (key !== undefined) {
        return `/${key}`;
    }

    return '';
};
/** @namespace Bodypwa/Hooks/UseSeoUrlHelpers/useSeoUrlHelpers */
export const useSeoUrlHelpers = () => {
    const { location } = history;
    const filterExist = (key, filters) => typeof filters[key] !== 'undefined';
    const filters = useSelector((state) => state.ProductListInfoReducer.filters);
    const seoFilterParams = useSelector((state) => state.UrlRewritesReducer.urlRewrite.params);
    const suffix = useSelector((state) => state.ConfigReducer.category_url_suffix || '.html');
    const filter_key = useSelector((state) => getFilterKey(state.ConfigReducer.filter_key));

    const selectedCustomFiltersValues = useMemo(() => {
        const selectedFiltersString = decodeURIComponent(getQueryParam('customFilters', location) || '').split(';');

        if (!selectedFiltersString || selectedFiltersString[0] === '') {
            return {};
        }

        return selectedFiltersString.reduce((acc, filter) => {
            if (!filter) {
                return acc;
            }
            const [key, value] = filter.split(':');
            return { ...acc, [key]: value?.split(',') || [] };
        }, {});
    }, [location]);

    const getNewFilterArray = useCallback((requestVar, value, customFiltersValues) => {
        const current = customFiltersValues[requestVar] || [];

        if (requestVar === KEY_PRICE) {
            return current.includes(value) ? [] : [value];
        }

        return current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value];
    }, []);

    const getSeoUrl = (filterName, filterValue, seoFilterParams) => {
        const pathname = location.pathname.replace(suffix, '');
        const { search = '' } = location;

        if (seoFilterParams) {
            return `${pathname}-${filterName}-${filterValue}${suffix}${search}`;
        }

        return `${pathname}${filter_key}/${filterName}-${filterValue}${suffix}${search}`;
    };

    const removeSeoPart = (filterName) => {
        const pathname = location.pathname.replace(suffix, '');
        const { search = '' } = location;
        const seoPart = pathname.split('/').pop();
        const pathWithoutSeoPart = pathname.replace(`${filter_key}/${seoPart}`, '');
        const seoPartArray = seoPart.split('-');
        const filterIndex = seoPartArray.indexOf(filterName);
        const newSeoPartArray = seoPartArray.filter((_, index) => index !== filterIndex && index !== filterIndex + 1);

        return newSeoPartArray.length
            ? `${pathWithoutSeoPart}${filter_key}/${newSeoPartArray.join('-')}${suffix}${search}`
            : `${pathWithoutSeoPart}${suffix}${search}`;
    };

    const changeSeoPart = (filterName, value) => {
        const pathname = location.pathname.replace(suffix, '');
        const { search = '' } = location;
        const seoPart = pathname.split('/').pop();
        const pathWithoutSeoPart = pathname.replace(`${filter_key}/${seoPart}`, '');
        const seoPartArray = seoPart.split('-');
        const filterIndex = seoPartArray.indexOf(filterName);

        const newSeoPartArray = seoPartArray.map((part, index) => (index === filterIndex + 1 ? value : part));

        return `${pathWithoutSeoPart}${filter_key}/${newSeoPartArray.join('-')}${suffix}${search}`;
    };

    const getFilterArrayNormal = (filterArray) => {
        const data = [];
        data.push(filterArray[filterArray.length - 1]);
        const stringLen = data[0].length;
        const secondValue = filterArray.join('').slice(0, -stringLen);
        data.push(secondValue);

        return data;
    };

    const getFilterAction = (filterArray = []) => {
        if (filterArray.length === 1) {
            return ADD;
        }
        if (filterArray.length > 1) {
            const filterArrayNormal = getFilterArrayNormal(filterArray);

            if (filterArrayNormal[0] === filterArrayNormal[1]) {
                return REMOVE;
            }

            return CHANGE;
        }

        return '';
    };

    const getSelectedFiltersFromUrl = () => {
        const selectedFiltersString = decodeURIComponent(
            getQueryParam('customFilters', location) || ''
        ).split(';');

        return selectedFiltersString.reduce((acc, filter) => {
            if (!filter) {
                return acc;
            }

            const [key, value] = filter.split(':');

            if (!key || value === undefined) {
                return acc;
            }

            return {
                ...acc,
                [key]: value.split(',').filter(Boolean)
            };
        }, {});
    };

    const getNewSelectedFiltersString = (filterName, filterArray) => {
        const prevCustomFilters = getSelectedFiltersFromUrl();
        const customFilers = {
            ...prevCustomFilters,
            [filterName]: filterArray
        };

        return Object.entries(customFilers)
            .reduce((accumulator, [filterKey, filterValue]) => {
                if (filterValue.length) {
                    const filterValues = filterValue.sort().join(',');

                    accumulator.push(`${filterKey}:${filterValues}`);
                }

                return accumulator;
            }, [])
            .sort()
            .join(';');
    };

    const formatSelectedFiltersString = (string) => {
        const hasTrailingSemicolon = string[string.length - 1] === ';';
        const hasLeadingSemicolon = string[0] === ';';

        if (hasLeadingSemicolon) {
            return formatSelectedFiltersString(string.slice(0, -1));
        }

        if (hasTrailingSemicolon) {
            return string.slice(1);
        }

        return string;
    };

    const getFilterUrl = (filterName, filterArray, isFull = false) => {
        const { pathname } = location;

        const action = getFilterAction(filterArray);
        if (filterExist(filterName, filters) && filters[filterName].isSeo) {
            const { attribute_options } = filters[filterName];
            switch (action) {
                case ADD:
                    const filterValue = attribute_options[filterArray[0]].label.toLowerCase();
                    const seoUrl = getSeoUrl(filterName, filterValue, seoFilterParams, location, suffix, filter_key);
                    return seoUrl;

                case REMOVE:
                    const removeSeoUrl = removeSeoPart(filterName, location, suffix, filter_key);
                    return removeSeoUrl;
                case CHANGE:
                    const value = attribute_options[filterArray[filterArray.length - 1]].label.toLowerCase();
                    const changeSeoUrl = changeSeoPart(
                        filterName, value, seoFilterParams, location, suffix, filter_key
                    );

                    return changeSeoUrl;
                default:
                    console.error(__('Something went Wrong'));
            }
        }

        const selectedFilters = getNewSelectedFiltersString(filterName, filterArray);
        const customFilters = isFull ? `${pathname}?customFilters=` : '';
        const formattedFilters = formatSelectedFiltersString(selectedFilters);

        return `${customFilters}${formattedFilters}`;
    };

    const updateFilter = useCallback(({ attribute_code: filterName, attribute_value }) => {
        const filterArray = getNewFilterArray(filterName, attribute_value, selectedCustomFiltersValues);
        const isSeo = filters[filterName]?.isSeo;
        if (!isSeo) {
            setQueryParams({
                customFilters: getFilterUrl(filterName, filterArray, false),
                page: ''
            }, location, history);
        } else {
            const action = getFilterAction(filterArray);

            if (filterExist(filterName, filters)) {
                const { attribute_options } = filters[filterName];

                switch (action) {
                    case ADD:
                        const filterValue = attribute_options[filterArray[0]].label.toLowerCase();
                        const seoUrl = getSeoUrl(
                            filterName, filterValue, seoFilterParams, location, suffix, filter_key
                        );

                        history.push(seoUrl);
                        break;
                    case REMOVE:
                        const removeSeoUrl = removeSeoPart(filterName, location, suffix, filter_key);
                        history.push(removeSeoUrl);
                        break;
                    case CHANGE:
                        const value = attribute_options[filterArray[filterArray.length - 1]].label.toLowerCase();
                        const changeSeoUrl = changeSeoPart(
                            filterName, value, seoFilterParams, location, suffix, filter_key
                        );

                        history.push(changeSeoUrl);
                        break;
                    default:
                        console.error(__('Something went Wrong'));
                }
            }
        }
    }, [filters, location, history, suffix, seoFilterParams, filter_key]);

    const getQuickFilterItems = useMemo(() => Object.values(filters)
            .filter((filter) => filter.is_quick_filter)
            .flatMap((filter) => Object.values(filter.attribute_options)
                    .filter((item) => item.is_quick_filter)
                    .map((item) => {
                        const { value_string, quick_filter_position, ...rest } = item;
                        return {
                            ...filter,
                            ...rest,
                            attribute_value: value_string,
                            quick_filter_position
                        };
                    }))
            .sort((a, b) => a.quick_filter_position - b.quick_filter_position),
        [filters]);

    return {
        getSeoUrl,
        removeSeoPart,
        changeSeoPart,
        getFilterUrl,
        updateFilter,
        quickFilters: getQuickFilterItems,
        selectedCustomFiltersValues,
        getSelectedFiltersFromUrl
    };
};
